/**
 * The AnimationDirector (frontend.md §3.2).
 *
 * A single serialized queue. Server events never animate concurrently and never
 * race the store.
 *
 * Two invariants, unchanged from M3 and load-bearing for the whole milestone:
 *
 *  1. **Input is never gated on animation.** Nothing reads `busy` to disable a
 *     button. The director orders visuals; it never delays play. The single
 *     exception the design allows is the órdago freeze, which sets its own flag
 *     (`anim.frozen`) and sets it only on the clients of the responding pair.
 *  2. **A broken timeline cannot stall the game.** Every entry races a 4 s
 *     watchdog and every rejection is swallowed, so the worst case is a missing
 *     animation, never a dead table.
 *
 * `animationLevel` maps to `gsap.globalTimeline.timeScale(1 | 2.2 | 8)`, so one
 * assignment speeds up every tween in flight — including one that is halfway
 * through when the player changes the setting.
 */

import type { Seat } from '@ordago/rules';

import { useStore } from '../../store';
import type { AnimationLevel, AnimEvent, FlipState } from '../../store/types';

import { clearWillChange, promote } from './dom';

const WATCHDOG_MS = 4_000;

/** What the running timeline is multiplied by when the player asks to skip. */
const SKIP_SCALE = 6;

/**
 * The GSAP-dependent half, installed by `<AnimationHost/>` when the table
 * mounts.
 *
 * The director itself imports no GSAP, and that is a bundling decision with
 * teeth: `bindRoom` imports the director, and `bindRoom` is reachable from the
 * lobby and the auth screens — so one value import of GSAP here would put the
 * whole library in the entry chunk and make the login page download the table's
 * animation engine. With the engine injected, GSAP is reachable only from the
 * lazily-routed room.
 *
 * Everything on it is optional-chained at the call site: before the table
 * mounts there is nothing to animate, so "no engine" is a normal state rather
 * than an error.
 */
export interface AnimEngine {
  applyTimeScale: (scale: number) => void;
  captureFlip: () => FlipState | null;
}

let engine: AnimEngine | null = null;

/** §3.2: full 1×, fast 2.2×, minimal 8× — 8× is "effectively instant, still ordered". */
const TIME_SCALE: Record<AnimationLevel, number> = { full: 1, fast: 2.2, minimal: 8 };

export interface DirectorContext {
  mySeat: Seat | null;
}

export type Timeline = (event: AnimEvent, ctx: DirectorContext) => Promise<void>;

const TIMELINES = new Map<string, Timeline>();

const queue: AnimEvent[] = [];
let running = false;
let context: DirectorContext = { mySeat: null };
let level: AnimationLevel = 'full';

/**
 * The timeline currently on screen, so `skip()` can fast-forward it and
 * `clear()` can kill it. Tracked rather than searched for: `gsap.globalTimeline`
 * also holds every unrelated tween, and killing that would take the HUD's
 * springs with it.
 */
let active: gsap.core.Timeline | null = null;
/** Nodes the running timeline promoted, so the promotion is always undone. */
let promoted: Element[] = [];

/**
 * When the running entry stops being given the benefit of the doubt. Extended by
 * `track()` to whatever the timeline says it needs; never shortened.
 */
let deadline = 0;

/**
 * The watchdog, as a poll against a deadline rather than one fixed timeout.
 *
 * A flat 4 s cap is right for "this timeline is broken" and wrong for "this
 * timeline is long". The reveal is legitimately ~5.5 s — sixteen cards turning
 * over, then four lances at 900 ms each — and under a fixed cap it was being cut
 * off after grande and chica, with the celebration queued behind it arriving
 * four seconds late. Both were visible in a screenshot: no confetti.
 *
 * So a timeline that declares its length through `track()` gets that length plus
 * a second of slack, and one that declares nothing — or hangs without ever
 * starting — still hits the 4 s floor.
 */
function watchdog<T>(promise: Promise<T>): Promise<void> {
  deadline = performance.now() + WATCHDOG_MS;
  return Promise.race([
    promise.then(() => undefined),
    new Promise<void>((resolve) => {
      const poll = (): void => {
        if (performance.now() >= deadline) {
          resolve();
          return;
        }
        setTimeout(poll, 200);
      };
      setTimeout(poll, 200);
    }),
  ]);
}

async function drain(): Promise<void> {
  running = true;
  const setBusy = useStore.getState().anim.setBusy;
  while (queue.length > 0) {
    const event = queue.shift();
    if (event === undefined) break;
    const timeline = TIMELINES.get(event.t);
    if (timeline === undefined) continue;
    setBusy(true, event.t);
    try {
      await watchdog(timeline(event, context));
    } catch {
      // Never let one timeline stop the queue.
    } finally {
      // Whatever happened — completed, watchdogged, threw — the compositor
      // promotions, the tracked handle and the skip affordance all belong to a
      // timeline that is over.
      //
      // `skippable` is cleared HERE rather than in each timeline's own
      // `finally`, because a timeline that throws before its `try` leaves the
      // button on screen with nothing behind it — which is exactly what a
      // screenshot caught: "Saltar" still offered while the next hand was being
      // dealt. One choke point that always runs beats two that usually do.
      clearWillChange(promoted);
      promoted = [];
      active = null;
      useStore.getState().anim.setSkippable(false);
    }
  }
  setBusy(false, null);
  running = false;
}

export const director = {
  push(event: AnimEvent): void {
    queue.push(event);
    if (!running) void drain();
  },

  register(type: string, timeline: Timeline): void {
    TIMELINES.set(type, timeline);
  },

  /** Who "I" am, so a timeline can tell my hand from the other three. */
  setContext(next: DirectorContext): void {
    context = next;
  },

  /** Installed by `<AnimationHost/>`; removed when the table unmounts. */
  setEngine(next: AnimEngine | null): void {
    engine = next;
    next?.applyTimeScale(scaleFor(level));
  },

  setLevel(next: AnimationLevel): void {
    level = next;
    engine?.applyTimeScale(scaleFor(next));
  },

  /**
   * The Flip capture (§3.3), called from the message handler that owns the
   * mutation — before it. A no-op when the table is not mounted, which is
   * exactly right: there are no cards to reparent.
   */
  captureFlip(): void {
    const state = engine?.captureFlip() ?? null;
    if (state !== null) useStore.getState().table.captureFlip(state);
  },

  level(): AnimationLevel {
    return level;
  },

  /**
   * Registers the timeline that is on screen right now. Timelines call this so
   * the reveal's "Saltar" button and a room teardown have something to act on.
   */
  track(timeline: gsap.core.Timeline, nodes: Element[] = []): void {
    active = timeline;
    promoted = nodes;
    promote(nodes);
    // The timeline knows how long it is; the watchdog does not. Extending the
    // deadline here is what lets the reveal run to its last lance without
    // weakening the cap for anything that hangs.
    const needs = (timeline.duration() * 1_000) / scaleFor(level) + WATCHDOG_MS;
    deadline = Math.max(deadline, performance.now() + needs);
  },

  /** The "Saltar" affordance and the double-tap: run what is left at 6×. */
  skip(): void {
    if (active === null) return;
    active.timeScale(SKIP_SCALE);
    // Skipping shortens the timeline, so the extra rope the watchdog gave it is
    // no longer needed — but it must never be pulled below the floor.
    const left = ((active.duration() - active.time()) * 1_000) / SKIP_SCALE;
    deadline = Math.min(deadline, performance.now() + left + WATCHDOG_MS);
  },

  /** True while something skippable is on screen, so the button can hide. */
  isSkippable(): boolean {
    return active !== null;
  },

  /** Called when a room is left, so a queued deal cannot fire onto a dead tree. */
  clear(): void {
    queue.length = 0;
    active?.kill();
    clearWillChange(promoted);
    promoted = [];
    active = null;
    useStore.getState().anim.setFrozen(false);
  },
};

/**
 * The e2e affordance (frontend.md §8.2): collapse motion without bypassing it.
 *
 * `timeScale(50)` rather than a branch that skips the timelines, deliberately.
 * The suite is meant to exercise the real code path — the queue still drains,
 * every timeline still runs, `busy` still flips — so a timeline that would
 * throw or strand a flag in production throws in the suite too. A bypass would
 * test the bypass.
 *
 * Strict `=== true`, and not gated on `import.meta.env.DEV`: the suite runs
 * against a production `vite build`, so this has to work in a prod bundle
 * exactly as the debug hook at `debug.ts` already does.
 */
export function noAnimRequested(): boolean {
  return typeof window !== 'undefined' && window.__ORDAGO_NO_ANIM === true;
}

/** 50×, so a 5.5 s reveal is 110 ms — instant, still ordered, still real. */
const NO_ANIM_SCALE = 50;

/** The test flag outranks the setting; otherwise the level decides. */
function scaleFor(chosen: AnimationLevel): number {
  return noAnimRequested() ? NO_ANIM_SCALE : TIME_SCALE[chosen];
}

/**
 * The effective level: `prefers-reduced-motion: reduce` forces `minimal`, but
 * only until the player says otherwise in Settings — an explicit choice is a
 * statement about this app, and silently overriding it would be the same bug in
 * the other direction.
 */
export function effectiveLevel(chosen: AnimationLevel, explicit: boolean): AnimationLevel {
  if (explicit) return chosen;
  return prefersReducedMotion() ? 'minimal' : chosen;
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
